import { useCallback, useContext, useMemo, useState } from 'react';

import { NavigationConfirmationHandlerContext } from './NavigationConfirmationHandler';
import { NavigationConfirmationManagerContext } from './useNavigationConfirmationManager';

export interface NavigationConfirmationTriggerResult {
  isNavigatingAway: boolean;
  tryNavigatingAway: (onNavigationSuccess: () => void, onNavigationCancel?: () => void) => void;
  requiresUserConfirmationBeforeNavigation: () => boolean;
}

const useNavigationConfirmationTrigger = (
  environmentId: string,
): NavigationConfirmationTriggerResult => {
  const manager = useContext(NavigationConfirmationManagerContext);
  const { setHandler } = useContext(NavigationConfirmationHandlerContext);

  const [isNavigatingAway, setIsNavigatingAway] = useState(false);

  const submitDirtyForms = useCallback(async () => {
    const forms = manager.getAllFormsInEnvironment(environmentId);
    const submitPromises = forms.map((form) => Promise.resolve(form.submit()));

    await Promise.all(submitPromises);
  }, [environmentId, manager]);

  const cancelDirtyForms = useCallback(() => {
    const forms = manager.getAllFormsInEnvironment(environmentId);

    forms.forEach((form) => form.onCancelledNavigation?.());
  }, [environmentId, manager]);

  const hasSubmitFailed = useCallback(
    (): boolean =>
      manager.getAllFormsInEnvironment(environmentId).some((form) => form.hasSubmitFailed()),
    [environmentId, manager],
  );

  const hasSubmitInterrupted = useCallback(
    (): boolean =>
      manager.getAllFormsInEnvironment(environmentId).some((form) => form.hasSubmitInterrupted?.()),
    [environmentId, manager],
  );

  const discardChanges = useCallback(() => {
    const forms = manager.getAllFormsInEnvironment(environmentId);

    forms.forEach((form) => form.onDiscardChanges?.());
  }, [environmentId, manager]);

  const submitResume = useCallback(
    (callback: () => void) => {
      const forms = manager.getAllFormsInEnvironment(environmentId);

      forms.forEach((form) => form.onSubmitResume?.(callback));
    },
    [environmentId, manager],
  );

  const requiresUserConfirmationBeforeNavigation = useCallback(
    () => manager.getAllFormsInEnvironment(environmentId).length > 0,
    [environmentId, manager],
  );

  const tryNavigatingAway = useCallback(
    (onNavigationSuccess: () => void, onNavigationCancel?: () => void) => {
      if (!requiresUserConfirmationBeforeNavigation()) {
        onNavigationSuccess();
        return;
      }

      setIsNavigatingAway(true);

      setHandler({
        cancel: () => {
          cancelDirtyForms();
          setIsNavigatingAway(false);
          if (onNavigationCancel) onNavigationCancel();
        },
        discard: () => {
          discardChanges();
          onNavigationSuccess();
          setIsNavigatingAway(false);
        },
        save: async () => {
          await submitDirtyForms();
          // Wait for next tick to ensure that form status is updated, without this e2e tests fail
          setTimeout(() => {
            if (!hasSubmitFailed()) {
              if (hasSubmitInterrupted()) {
                submitResume(() => {
                  onNavigationSuccess();
                  setIsNavigatingAway(false);
                });
              } else {
                onNavigationSuccess();
                setIsNavigatingAway(false);
              }
            }
          });
        },
      });
    },
    [
      requiresUserConfirmationBeforeNavigation,
      setHandler,
      cancelDirtyForms,
      discardChanges,
      submitDirtyForms,
      hasSubmitFailed,
      hasSubmitInterrupted,
      submitResume,
    ],
  );

  return useMemo<NavigationConfirmationTriggerResult>(
    () => ({
      tryNavigatingAway,
      isNavigatingAway,
      requiresUserConfirmationBeforeNavigation,
    }),
    [isNavigatingAway, tryNavigatingAway, requiresUserConfirmationBeforeNavigation],
  );
};

export default useNavigationConfirmationTrigger;
